import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './index.css';
import { Form } from '@formio/react';
import Loader from '../../../components/common/Loader';
import { Paper } from '../../../components/common/Containers';
import Typography from '../../../components/common/Typography';
import { Button } from '../../../components/common/Buttons';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useTrans } from '../../../services/i18n';
import connect from '../connect';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Box from '@material-ui/core/Box';
import CongratulationsModal from '../../../components/common/Modals/CongratulationsModal';

const RESPONSE_ID = 'formsAssignment';
const SAM_REGISTRATION_FORM_NAME = 'samRegistration';
const VOSB_CLIENT_ASSESSMENT_FORM_NAME = 'vosbClientAssessment';
const GSA_SCHEDULE_ASSESSMENT_FORM_NAME = 'GSAScheduleAssessment';
const HUBZONE_CLIENT_ASSESSMENT_FORM_NAME = 'hubzoneClientAssessment';
const WOSB_CLIENT_ASSESSMENT_FORM_NAME = 'wosbClientAssessment';
const SAP_FORM_NAME = 'sap';
const SAM_KEY_ALIASES = {
  legalBusinessName: ['Legal_Business_Name', 'legal_business_name', 'legalBusinessName'],
  uei: ['Uei', 'uei', 'ueiCode'],
  cage: ['Cage', 'cage', 'cageCode'],
  address1: ['Physical_Address', 'physical_address', 'address', 'Address'],
  address2: ['Physical_Address_Line_2', 'physical_address_line_2', 'address2', 'Address_2'],
  city: ['Physical_Address_City', 'physical_address_city', 'city', 'City'],
  state: ['Physical_Address_State', 'physical_address_state', 'state', 'State'],
  zip: ['Physical_Address_Zip', 'Physical_Address_ZIP', 'physical_address_zip', 'zip', 'Zip'],
  country: ['Physical_Address_Country', 'physical_address_country', 'country', 'Country'],
  arPhone: ['Accounts_Receivable_POC_Phone', 'accounts_receivable_poc_phone', 'accountsReceivablePocPhone', 'phone'],
};

const FormDetail = ({
  getFormStackById,
  formSubmission,
  formPartialSubmission,
  downloadFormPdf,
  getOrganizationInfo,
  org,
}) => {
  const { id } = useParams();
  const { successAlert, errorAlert } = useAlertContext();
  const { trans } = useTrans();
  const history = useHistory();

  const [formDef, setFormDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partialData, setPartialData] = useState({});
  const [currentFormData, setCurrentFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingPartial, setIsSavingPartial] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isFormReady, setIsFormReady] = useState(false);
  const [showCongratulationsModal, setShowCongratulationsModal] = useState(false);
  const isInitialMountRef = React.useRef(true);

  const findComponentByKey = (components, targetKey) => {
    if (!Array.isArray(components)) return null;

    for (const component of components) {
      if (component.key === targetKey) return component;

      if (component.components) {
        const found = findComponentByKey(component.components, targetKey);
        if (found) return found;
      }

      if (component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            const found = findComponentByKey(column.components, targetKey);
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  const collectFormFieldKeys = (components, depth = 0) => {
    const keys = [];
    if (!Array.isArray(components)) return keys;

    for (const component of components) {
      if (
        component.key &&
        component.input !== false &&
        component.type !== 'button' &&
        component.type !== 'content' &&
        component.type !== 'htmlelement'
      ) {
        keys.push(component.key);
      }

      if (component.type === 'columns' && component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            keys.push(...collectFormFieldKeys(column.components, depth + 1));
          }
        }
      } else if (component.type === 'fieldset' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      } else if (component.type === 'panel' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      } else if (component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      }
    }
    return keys;
  };

  const pickFirstValue = (...values) => {
    const nonEmptyValue = values.find((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true;
    });
    return typeof nonEmptyValue === 'string' ? nonEmptyValue.trim() : nonEmptyValue;
  };

  const splitName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return { first: '', last: '' };
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first: '', last: '' };
    if (parts.length === 1) return { first: parts[0], last: '' };
    return {
      first: parts[0],
      last: parts.slice(1).join(' '),
    };
  };

  const findExistingSamKey = (form, aliases = []) => aliases.find((key) => findComponentByKey(form?.components, key));

  const prefillSamFields = (form, savedData, organizationInfo) => {
    const isSamRegistration = form?.name === SAM_REGISTRATION_FORM_NAME;
    if (!isSamRegistration) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};

    const setIfEmpty = (aliases, value) => {
      const targetKey = findExistingSamKey(form, aliases);
      if (!targetKey) return;
      if (pickFirstValue(nextData?.[targetKey])) return;
      if (!pickFirstValue(value)) return;
      nextData[targetKey] = value;
    };

    setIfEmpty(
      SAM_KEY_ALIASES.legalBusinessName,
      pickFirstValue(details.companyName, details.legalBusinessName, organizationInfo?.orgName)
    );
    setIfEmpty(SAM_KEY_ALIASES.uei, pickFirstValue(details.ueiCode, organizationInfo?.vendor?.ueiCode));
    setIfEmpty(SAM_KEY_ALIASES.cage, pickFirstValue(details.cageCode, organizationInfo?.vendor?.cageCode));
    setIfEmpty(SAM_KEY_ALIASES.address1, details.physicalAddress || details.addressLine1);
    setIfEmpty(SAM_KEY_ALIASES.address2, details.addressLine2);
    setIfEmpty(SAM_KEY_ALIASES.city, details.city);
    setIfEmpty(SAM_KEY_ALIASES.state, details.state);
    setIfEmpty(SAM_KEY_ALIASES.zip, details.zipCode);
    setIfEmpty(SAM_KEY_ALIASES.country, details.country);
    setIfEmpty(SAM_KEY_ALIASES.arPhone, pickFirstValue(details.phoneNumber, organizationInfo?.contactPhoneNumber));

    if (Array.isArray(details.primaryNaics) && details.primaryNaics.length > 0) {
      setIfEmpty(
        ['Primary_NAICS', 'primary_naics', 'what_is_the_companys_primary_six_6_digit_naics_code'],
        details.primaryNaics.join('\n')
      );
    }

    if (Array.isArray(details.secondaryNaics) && details.secondaryNaics.length > 0) {
      setIfEmpty(
        [
          'Secondary_NAICS',
          'secondary_naics',
          'list_any_secondary_naics_codes_or_provide_a_detailed_description_of_what_your_company_provides',
        ],
        details.secondaryNaics.join('\n')
      );
    }

    return nextData;
  };

  const prefillVosbAssessmentFields = (form, savedData, organizationInfo) => {
    const isVosbAssessment = form?.name === VOSB_CLIENT_ASSESSMENT_FORM_NAME;
    if (!isVosbAssessment) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};
    const { first, last } = splitName(organizationInfo?.contactPerson);

    const setIfEmptyDirectKey = (key, value) => {
      if (!findComponentByKey(form?.components, key)) return;
      if (pickFirstValue(nextData?.[key])) return;
      if (!pickFirstValue(value)) return;
      nextData[key] = value;
    };

    setIfEmptyDirectKey('acctId', organizationInfo?.associatedSalesforceId);
    setIfEmptyDirectKey('email', organizationInfo?.contactEmail);
    setIfEmptyDirectKey('phone', pickFirstValue(organizationInfo?.contactPhoneNumber, details.phoneNumber));
    setIfEmptyDirectKey('company', pickFirstValue(organizationInfo?.orgName, details.companyName));
    setIfEmptyDirectKey('name1', organizationInfo?.contactPerson);
    setIfEmptyDirectKey('name_of_company', pickFirstValue(details.companyName, organizationInfo?.orgName));

    if (findComponentByKey(form?.components, 'fullName')) {
      const existingFirst = pickFirstValue(nextData?.fullName?.first);
      const existingLast = pickFirstValue(nextData?.fullName?.last);
      if (!existingFirst || !existingLast) {
        nextData.fullName = {
          first: existingFirst || first,
          last: existingLast || last,
        };
      }
    }

    return nextData;
  };

  const prefillGsaAssessmentFields = (form, savedData, organizationInfo) => {
    const isGsaAssessment = form?.name === GSA_SCHEDULE_ASSESSMENT_FORM_NAME;
    if (!isGsaAssessment) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};
    const { first, last } = splitName(organizationInfo?.contactPerson);

    const setIfEmptyDirectKey = (key, value) => {
      if (!findComponentByKey(form?.components, key)) return;
      if (pickFirstValue(nextData?.[key])) return;
      if (!pickFirstValue(value)) return;
      nextData[key] = value;
    };

    setIfEmptyDirectKey('accountId', organizationInfo?.associatedSalesforceId);
    setIfEmptyDirectKey('firstName', first);
    setIfEmptyDirectKey('columnsLastname', last);
    setIfEmptyDirectKey('phoneNumber', pickFirstValue(organizationInfo?.contactPhoneNumber, details.phoneNumber));
    setIfEmptyDirectKey('email', organizationInfo?.contactEmail);
    setIfEmptyDirectKey('name_of_company', pickFirstValue(details.companyName, organizationInfo?.orgName));

    // "title" here is contact title; no dedicated source currently, so fallback to contact person title if present.
    setIfEmptyDirectKey('title', pickFirstValue(details.contactTitle, details.jobTitle));

    return nextData;
  };

  const prefillHubzoneAssessmentFields = (form, savedData, organizationInfo) => {
    const isHubzoneAssessment = form?.name === HUBZONE_CLIENT_ASSESSMENT_FORM_NAME;
    if (!isHubzoneAssessment) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};
    const { first, last } = splitName(organizationInfo?.contactPerson);

    const setIfEmptyDirectKey = (key, value) => {
      if (!findComponentByKey(form?.components, key)) return;
      if (pickFirstValue(nextData?.[key])) return;
      if (!pickFirstValue(value)) return;
      nextData[key] = value;
    };

    setIfEmptyDirectKey('accountid', organizationInfo?.associatedSalesforceId);
    setIfEmptyDirectKey('email', organizationInfo?.contactEmail);
    setIfEmptyDirectKey('phone', pickFirstValue(organizationInfo?.contactPhoneNumber, details.phoneNumber));
    setIfEmptyDirectKey('company', pickFirstValue(details.companyName, organizationInfo?.orgName));
    setIfEmptyDirectKey('name', organizationInfo?.contactPerson);

    setIfEmptyDirectKey('first_name', first);
    setIfEmptyDirectKey('last_name', last);
    setIfEmptyDirectKey('contact_title', pickFirstValue(details.contactTitle, details.jobTitle));
    setIfEmptyDirectKey('contact_company', pickFirstValue(details.companyName, organizationInfo?.orgName));

    setIfEmptyDirectKey('registered_state', details.state);
    setIfEmptyDirectKey(
      'primary_naics_code',
      Array.isArray(details.primaryNaics) ? details.primaryNaics[0] : undefined
    );

    setIfEmptyDirectKey(
      'address_of_the_principal_office',
      pickFirstValue(details.physicalAddress, details.addressLine1)
    );
    setIfEmptyDirectKey('address_of_the_principal_office_line_2', details.addressLine2);
    setIfEmptyDirectKey('address_of_the_principal_office_city', details.city);
    setIfEmptyDirectKey('address_of_the_principal_office_state', details.state);
    setIfEmptyDirectKey('address_of_the_principal_office_zip', details.zipCode);

    return nextData;
  };

  const prefillWosbAssessmentFields = (form, savedData, organizationInfo) => {
    const isWosbAssessment = form?.name === WOSB_CLIENT_ASSESSMENT_FORM_NAME;
    if (!isWosbAssessment) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};
    const { first, last } = splitName(organizationInfo?.contactPerson);

    const setIfEmptyDirectKey = (key, value) => {
      if (!findComponentByKey(form?.components, key)) return;
      if (pickFirstValue(nextData?.[key])) return;
      if (!pickFirstValue(value)) return;
      nextData[key] = value;
    };

    setIfEmptyDirectKey('accountId', organizationInfo?.associatedSalesforceId);
    setIfEmptyDirectKey('email', organizationInfo?.contactEmail);
    setIfEmptyDirectKey('first_name', first);
    setIfEmptyDirectKey('last_name', last);
    setIfEmptyDirectKey('title', pickFirstValue(details.contactTitle, details.jobTitle));
    setIfEmptyDirectKey('name_of_company', pickFirstValue(details.companyName, organizationInfo?.orgName));
    setIfEmptyDirectKey('regState', details.state);
    setIfEmptyDirectKey('primary_naics', Array.isArray(details.primaryNaics) ? details.primaryNaics[0] : undefined);

    return nextData;
  };

  const prefillSapFields = (form, savedData, organizationInfo) => {
    const isSapForm = form?.name === SAP_FORM_NAME;
    if (!isSapForm) return savedData;

    const nextData = { ...savedData };
    const details = organizationInfo?.details || {};
    const { first, last } = splitName(organizationInfo?.contactPerson);

    const setIfEmptyDirectKey = (key, value) => {
      if (!findComponentByKey(form?.components, key)) return;
      if (pickFirstValue(nextData?.[key])) return;
      if (!pickFirstValue(value)) return;
      nextData[key] = value;
    };

    setIfEmptyDirectKey('accountId', organizationInfo?.associatedSalesforceId);
    setIfEmptyDirectKey('UEI', pickFirstValue(details.ueiCode, organizationInfo?.vendor?.ueiCode));
    setIfEmptyDirectKey(
      'Legal_Business_name',
      pickFirstValue(details.companyName, details.legalBusinessName, organizationInfo?.orgName)
    );
    setIfEmptyDirectKey('DBA', details.dba);
    setIfEmptyDirectKey('POC_First', first);
    setIfEmptyDirectKey('POC_Last', last);
    setIfEmptyDirectKey('POC_Email', organizationInfo?.contactEmail);
    setIfEmptyDirectKey('Phone', pickFirstValue(organizationInfo?.contactPhoneNumber, details.phoneNumber));
    setIfEmptyDirectKey('Corporate_Website_URL', details.website);

    return nextData;
  };

  const prepareFormSubmissionData = (formData = {}) => {
    const orderedData = {};

    if (formDef?.components) {
      const fieldKeys = collectFormFieldKeys(formDef.components);

      for (const key of fieldKeys) {
        if (formData.hasOwnProperty(key)) {
          orderedData[key] = formData[key];
        }
      }

      for (const [key, value] of Object.entries(formData)) {
        if (!orderedData.hasOwnProperty(key)) {
          orderedData[key] = value;
        }
      }
    } else {
      Object.assign(orderedData, formData);
    }

    if (org?.associatedSalesforceId) {
      orderedData.accountId = org.associatedSalesforceId;
    }

    if (formDef?.components) {
      const formTypeComponent = findComponentByKey(formDef.components, 'formType');
      if (formTypeComponent?.defaultValue) {
        orderedData.formType = formTypeComponent.defaultValue;
      }
    }

    return orderedData;
  };

  const onPartial = () => {
    if (isSavingPartial) return;

    setIsSavingPartial(true);
    const enhancedPartialData = prepareFormSubmissionData(partialData);
    formPartialSubmission({
      formType: 'principal',
      formUsfcrId: formDef.formUsfcrId,
      formStackId: formDef._id,
      currentSection: '',
      fields: enhancedPartialData,
    })
      .then(() => {
        successAlert(trans('form-progress-saved'));
        history.push('/client-portal-forms');
      })
      .catch(() => {
        errorAlert(trans('error-saving-form'));
        setIsSavingPartial(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    setIsFormReady(false);
    isInitialMountRef.current = true;

    getFormStackById({ id, formType: 'principal' })
      .then(async (res) => {
        const form = res.payload.form;
        setFormDef(form);
        const savedPartialData = form.partialSubmission || {};

        const orgResponse = await getOrganizationInfo?.({ includeDetails: true });
        const organizationInfo = orgResponse?.payload?.orgInfo;
        const samPrefilledData = prefillSamFields(form, savedPartialData, organizationInfo);
        const vosbPrefilledData = prefillVosbAssessmentFields(form, samPrefilledData, organizationInfo);
        const gsaPrefilledData = prefillGsaAssessmentFields(form, vosbPrefilledData, organizationInfo);
        const hubzonePrefilledData = prefillHubzoneAssessmentFields(form, gsaPrefilledData, organizationInfo);
        const wosbPrefilledData = prefillWosbAssessmentFields(form, hubzonePrefilledData, organizationInfo);
        const prefilledData = prefillSapFields(form, wosbPrefilledData, organizationInfo);

        setPartialData(prefilledData);
        setCurrentFormData(prefilledData);
        setFormKey((prev) => prev + 1);
        setIsFormReady(true);
      })
      .catch(() => {
        errorAlert(trans('error-loading-form'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, getFormStackById, errorAlert, trans]);

  if (loading || !isFormReady) {
    return <Loader open />;
  }
  if (!formDef) {
    return <Typography>{trans('form-not-found')}</Typography>;
  }

  return (
    <>
      <Loader open={isSubmitting || isSavingPartial} />
      <Paper style={{ padding: 24 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push('/client-portal-forms')}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography variant="h6" style={{ marginLeft: 8 }}>
              {trans('cancel-and-quit')}
            </Typography>
          </Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {formDef.title}
          </Typography>

          <Button variant="contained" color="info" onClick={onPartial} style={{ minWidth: 160 }}>
            {trans('save-and-resume-later')}
          </Button>
        </Box>
        <div className="my-form-wrapper">
          <Form
            key={`form-${formDef._id}-${formKey}`}
            form={formDef}
            submission={{ data: currentFormData }}
            options={{
              disableAlerts: false,
            }}
            onChange={({ data }) => {
              // Ignore first onChange from FormIO initialization to preserve loaded data
              if (isInitialMountRef.current) {
                isInitialMountRef.current = false;
                return;
              }

              setPartialData(data);
              setCurrentFormData(data);
            }}
            onSubmit={({ data }) => {
              if (isSubmitting) return Promise.resolve();

              setIsSubmitting(true);

              const mergedData = { ...currentFormData, ...data };
              const enhancedSubmissionData = prepareFormSubmissionData(mergedData);

              return formSubmission({
                formFields: enhancedSubmissionData,
                usfcr_form_id: formDef._id,
                formUsfcrId: formDef.formUsfcrId,
                type: 'principal',
              })
                .then((res) => {
                  if (res?.payload?.assignment) {
                    localStorage.setItem(RESPONSE_ID, JSON.stringify(res?.payload?.assignment));
                  }

                  // Check if it's the SAM Registration form
                  const isSAMForm = formDef?.name === SAM_REGISTRATION_FORM_NAME;

                  if (isSAMForm) {
                    // Show congratulations modal for SAM form
                    setIsSubmitting(false);
                    setShowCongratulationsModal(true);
                  } else {
                    // For other forms, show alert and redirect immediately
                    successAlert(trans('form-submitted'));
                    history.push('/client-portal-forms');
                  }
                })
                .catch(() => {
                  errorAlert(trans('error-submitting-form'));
                  setIsSubmitting(false);
                });
            }}
            onError={() => {
              errorAlert(trans('error-submitting-form'));
            }}
          />
        </div>
      </Paper>

      <CongratulationsModal
        open={showCongratulationsModal}
        onClose={() => setShowCongratulationsModal(false)}
        onContinue={() => {
          successAlert(trans('form-submitted'));
          history.push('/client-portal-forms');
        }}
        title={trans('congratulations') || 'Congratulations!'}
        message={trans('sam-form-success-message') || 'You have successfully completed your SAM Registration!'}
        statusText={
          trans('sam-form-status-text') || 'Your submission has been received and is being processed by our team.'
        }
        badgeText={trans('sam-form-badge') || 'SAM Form Submitted'}
        continueButtonText={trans('continue-to-dashboard') || 'Continue to Dashboard'}
      />
    </>
  );
};

export default connect(FormDetail);
